import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const PageContainer = styled.div`
  padding-top: 80px; /* Account for fixed header */
`;

const HeroSection = styled.section`
  background-color: var(--black);
  color: var(--white);
  padding: 6rem 0;
  text-align: center;
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const PageTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const PageDescription = styled.p`
  font-size: 1.2rem;
  color: var(--gray-300);
  margin-bottom: 2rem;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const AboutContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 5rem 2rem;
`;

const StorySection = styled.section`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  margin-bottom: 6rem;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const StoryContent = styled.div``;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
  color: var(--black);
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const StoryText = styled.p`
  font-size: 1.1rem;
  color: var(--gray-700);
  margin-bottom: 1.5rem;
  line-height: 1.6;
`;

const StoryImage = styled(motion.div)`
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  
  img {
    width: 100%;
    height: auto;
    display: block;
  }
`;

const ValuesSection = styled.section`
  margin-bottom: 6rem;
`;

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const ValueCard = styled(motion.div)`
  background-color: var(--white);
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  border: 1px solid var(--gray-200);
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
    transform: translateY(-5px);
    border-color: var(--primary);
  }
`;

const ValueIcon = styled.div`
  width: 60px;
  height: 60px;
  background-color: var(--primary-light);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  color: var(--primary);
  font-size: 1.5rem;
`;

const ValueTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
`;

const ValueDescription = styled.p`
  color: var(--gray-700);
  line-height: 1.6;
`;

const AboutPage = () => {
  return (
    <PageContainer>
      <HeroSection>
        <HeroContent>
          <PageTitle style={{ fontFamily: 'Fira Code, monospace' }}>About Plat<span style={{ color: '#30e8be' }}>form0</span></PageTitle>
          <PageDescription>
            Architects, engineers, and AI specialists dedicated to helping businesses build better technology solutions.
          </PageDescription>
        </HeroContent>
      </HeroSection>
      
      <AboutContainer>
  <StorySection>
    <StoryContent>
      <SectionTitle>Our Story</SectionTitle>
      <StoryText>
        Platform<span style={{ fontFamily: 'Fira Code, monospace'}}>0</span> was founded with a simple but powerful mission: to make expert architectural and AI guidance accessible to businesses of all sizes. We saw firsthand how many organizations struggle to design scalable systems and harness AI’s potential effectively, often due to lack of specialized expertise or resources.
      </StoryText>
      <StoryText>
        With decades of experience and driven by a passion for innovation and practical impact, we created Platform0 to serve as a trusted partner that helps businesses navigate the complexities of modern digital architecture and AI strategy.
      </StoryText>
      <StoryText>
        We’re actively building partnerships with startups and enterprises across industries. Our goal is to empower organizations to build resilient, scalable, and intelligent systems, enabling growth, innovation, and competitive advantage in an ever-changing digital landscape.
      </StoryText>
    </StoryContent>
          <StoryImage
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <img src={require('../assets/images/aboutus.jpg')} alt="Platform0 Story" />
          </StoryImage>
        </StorySection>
        
        <ValuesSection>
          <SectionTitle style={{ textAlign: 'center', marginBottom: '3rem' }}>Our Values</SectionTitle>
          <ValuesGrid>
            <ValueCard
              whileHover={{ y: -10 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <ValueIcon>E</ValueIcon>
              <ValueTitle>Excellence</ValueTitle>
              <ValueDescription>
                We are committed to delivering the highest quality solutions that exceed our clients' expectations and stand the test of time.
              </ValueDescription>
            </ValueCard>
            
            <ValueCard
              whileHover={{ y: -10 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <ValueIcon>I</ValueIcon>
              <ValueTitle>Innovation</ValueTitle>
              <ValueDescription>
                We embrace new technologies and approaches, constantly pushing the boundaries of what's possible to deliver innovative solutions.
              </ValueDescription>
            </ValueCard>
            
            <ValueCard
              whileHover={{ y: -10 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <ValueIcon>C</ValueIcon>
              <ValueTitle>Collaboration</ValueTitle>
              <ValueDescription>
                We believe in working closely with our clients, understanding their needs, and building solutions together as partners.
              </ValueDescription>
            </ValueCard>
            
            <ValueCard
              whileHover={{ y: -10 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <ValueIcon>T</ValueIcon>
              <ValueTitle>Transparency</ValueTitle>
              <ValueDescription>
                We believe in open communication, clear expectations, and honest feedback throughout the entire process.
              </ValueDescription>
            </ValueCard>
            
            <ValueCard
              whileHover={{ y: -10 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <ValueIcon>R</ValueIcon>
              <ValueTitle>Responsibility</ValueTitle>
              <ValueDescription>
                We take ownership of our work and are committed to creating solutions that are ethical, sustainable, and responsible.
              </ValueDescription>
            </ValueCard>
            
            <ValueCard
              whileHover={{ y: -10 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              <ValueIcon>A</ValueIcon>
              <ValueTitle>Adaptability</ValueTitle>
              <ValueDescription>
                We embrace change and are quick to adapt to new challenges, technologies, and market conditions to deliver the best results.
              </ValueDescription>
            </ValueCard>
          </ValuesGrid>
        </ValuesSection>
        
        {/* <TeamSection>
          <SectionTitle style={{ textAlign: 'center', marginBottom: '3rem' }}>Our Team</SectionTitle>
          <TeamGrid>
            <TeamMember
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <MemberImage>
                <img src="https://via.placeholder.com/300x300/f5f5f5/30e8be?text=J" alt="John Smith" />
              </MemberImage>
              <MemberName>John Smith</MemberName>
              <MemberRole>CEO & Founder</MemberRole>
              <MemberBio>
                With 20+ years of experience in system architecture and AI, John leads Platform0's vision and strategy.
              </MemberBio>
            </TeamMember>
            
            <TeamMember
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <MemberImage>
                <img src="https://via.placeholder.com/300x300/f5f5f5/30e8be?text=S" alt="Sarah Johnson" />
              </MemberImage>
              <MemberName>Sarah Johnson</MemberName>
              <MemberRole>CTO</MemberRole>
              <MemberBio>
                Sarah oversees Platform0's technical direction, bringing her expertise in distributed systems and AI architecture.
              </MemberBio>
            </TeamMember>
            
            <TeamMember
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <MemberImage>
                <img src="https://via.placeholder.com/300x300/f5f5f5/30e8be?text=M" alt="Michael Chen" />
              </MemberImage>
              <MemberName>Michael Chen</MemberName>
              <MemberRole>Lead AI Consultant</MemberRole>
              <MemberBio>
                Michael leads our AI consultancy practice, with deep expertise in machine learning and natural language processing.
              </MemberBio>
            </TeamMember>
            
            <TeamMember
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <MemberImage>
                <img src="https://via.placeholder.com/300x300/f5f5f5/30e8be?text=E" alt="Emily Rodriguez" />
              </MemberImage>
              <MemberName>Emily Rodriguez</MemberName>
              <MemberRole>Principal Architect</MemberRole>
              <MemberBio>
                Emily specializes in cloud architecture and microservices, helping clients design scalable and resilient systems.
              </MemberBio>
            </TeamMember>
          </TeamGrid>
        </TeamSection> */}
      </AboutContainer>
    </PageContainer>
  );
};

export default AboutPage;
